/**
 * SpiffsFinanceTab — Finance Board tab for Shannon to record SPIFFs paid/unpaid.
 *
 * Lists every SPIFF (rep SPIFFs + ASM 50% overrides) grouped by week, newest
 * first. No manual/automatic distinction — every row is payable. Shannon marks
 * rows paid (single or whole-week bulk); paid rows can be reverted to unpaid.
 *
 * Reads/writes the `spiffs` table directly (RLS: finance + crew managers can
 * UPDATE the paid columns). Realtime-refreshes on any `spiffs` change.
 *
 * Oct 8, 2026 — "Send to Books": finance (or the approval tier) sends a
 * week's unpaid SPIFFs (or the ticked rows) through `spiffs_send_to_books`,
 * which makes ONE Books bill per person (`books_bill_id`); Shannon pays it in
 * Books and the pull marks the rows paid ('books:<method>'). A row on a bill
 * reads "sent to Books" and has no Mark paid; an unpaid row NOT on a bill
 * (legacy) keeps Mark paid / the whole-week button; a paid row can still be
 * reverted to unpaid here.
 *
 * Oct 27, 2026 — the Monday approval (20261027j_spiff_approval.sql): a SPIFF waits
 * for Alex (the ASM; Chad or Greg when he hasn't) to approve it, on the ASM board or
 * here; approving makes its Books bill. Finance no longer sends SPIFFs and a waiting
 * row has no Mark paid; it reads "waiting for Alex". The legacy Mark paid stays for an
 * approved row on no bill.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
import type { TeamRole } from "../../lib/types";
import {
  BILL_CHIP_CLASS,
  billChipText,
  billChipTone,
  fetchBooksPayoutBills,
  EMPTY_BILL_INDEX,
  type BillIndex,
} from "../../lib/booksPayoutBills";
import { useAuth } from "../../context/AuthContext";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import CommissionStatementsCard from "../commissions/CommissionStatementsCard";
import { fmtUSD } from "../../lib/analytics";
import {
  fetchFinanceSpiffs,
  setSpiffsPaid,
  type FinanceSpiffRow,
} from "../crew/crewQueries";
import { FIVE_STAR_REVIEW_SPIFF } from "../../lib/weeklySpiffs";
import { canApproveSpiffs, isWaiting } from "../../lib/spiffApproval";
import { approveSpiffs } from "../crew/crewQueries";

type PaidFilter = "unpaid" | "paid" | "all";
type WinKey = "30d" | "90d" | "6mo" | "ytd" | "all";

const WINDOWS: Array<{ key: WinKey; label: string }> = [
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
  { key: "6mo", label: "6 mo" },
  { key: "ytd", label: "This year" },
  { key: "all", label: "All" },
];

/** Lower-bound date (YYYY-MM-DD, local) for a window; null = no bound. */
function sinceFor(win: WinKey): string | null {
  if (win === "all") return null;
  const d = new Date();
  if (win === "ytd") return `${d.getFullYear()}-01-01`;
  const days = win === "30d" ? 30 : win === "90d" ? 90 : 182;
  d.setDate(d.getDate() - days);
  return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
}

const ROLE_SHORT: Record<string, string> = {
  asm: "ASM",
  team_lead: "TL",
  restore_manager: "RM",
};

function etDateOf(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}
function mondayOf(etDateStr: string): string {
  const d = new Date(etDateStr + "T12:00:00Z");
  const dow = d.getUTCDay(); // 0=Sun..6=Sat
  d.setUTCDate(d.getUTCDate() + (dow === 0 ? -6 : 1 - dow));
  return d.toISOString().slice(0, 10);
}
function fmtWeek(monIso: string): string {
  const mon = new Date(monIso + "T12:00:00Z");
  const sun = new Date(mon);
  sun.setUTCDate(sun.getUTCDate() + 6);
  const f = (x: Date) => x.toLocaleDateString("en-US", { timeZone: "UTC", month: "short", day: "numeric" });
  return `Week of ${f(mon)} – ${f(sun)}`;
}
function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric" });
}

interface WeekGroup {
  monday: string;
  rows: FinanceSpiffRow[];
  unpaidCents: number;
  paidCents: number;
  /** Unpaid and on a Books bill already (Oct 8, 2026). */
  sentCents: number;
  /** Waiting for approval (Oct 27, 2026). */
  waitingCents: number;
}

// Who sees the SPIFF statement emails card (finance and the executives).
function canSeeStatements(role: TeamRole | undefined, isSuperUser: boolean): boolean {
  return isSuperUser || role === "finance" || role === "admin" || role === "sales_director";
}

export default function SpiffsFinanceTab() {
  const { member, role, isSuperUser } = useAuth();
  const [rows, setRows] = useState<FinanceSpiffRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<PaidFilter>("unpaid");
  const [win, setWin] = useState<WinKey>("90d");
  const [busy, setBusy] = useState<Set<string>>(new Set());
  // Oct 8, 2026 — ticked unpaid rows for "Send selected to Books".
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // The Books bills behind the rows on a bill (chip text); bill id → bill.
  const [bills, setBills] = useState<BillIndex>(EMPTY_BILL_INDEX);
  // Oct 27, 2026 — approving is sending; mirrors books_payout_approve_tier('spiff') (roster role).
  const sendAllowed = canApproveSpiffs(role);
  const statementsAllowed = canSeeStatements(role, isSuperUser);

  const sinceDate = useMemo(() => sinceFor(win), [win]);

  const load = useCallback(async () => {
    setErr(null);
    try {
      setRows(await fetchFinanceSpiffs(sinceDate));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load SPIFFs");
      setRows([]);
    }
  }, [sinceDate]);

  useEffect(() => {
    load();
  }, [load]);
  useRealtimeTable("spiffs", load);
  useRealtimeTable("books_payout_bills", load);

  // The bills the loaded rows point at (one query by id).
  useEffect(() => {
    const ids = [...new Set((rows ?? []).map((r) => r.books_bill_id).filter((x): x is string => !!x))];
    if (ids.length === 0) { setBills(EMPTY_BILL_INDEX); return; }
    let cancelled = false;
    fetchBooksPayoutBills({ billIds: ids, liveOnly: false })
      .then((ix) => { if (!cancelled) setBills(ix); })
      .catch((e: unknown) => { if (!cancelled) setErr(`Couldn't read the Books bills: ${formatSupabaseError(e)}`); });
    return () => { cancelled = true; };
  }, [rows]);

  // Oct 8, 2026 — one bill per person for the rows given. Oct 27, 2026 — this is
  // approving them (spiffs_approve), so only waiting rows are passed.
  async function sendToBooks(ids: string[]) {
    const sendable = ids.filter((id) => { const r = (rows ?? []).find((x) => x.id === id); return !!r && isWaiting(r); });
    if (!member || sendable.length === 0) return;
    setBusy((prev) => new Set([...prev, ...sendable]));
    setErr(null);
    try {
      await approveSpiffs(sendable);
      setSelected((prev) => { const n = new Set(prev); sendable.forEach((id) => n.delete(id)); return n; });
      await load();
    } catch (e) {
      setErr(`Couldn't approve: ${formatSupabaseError(e)}`);
    } finally {
      setBusy((prev) => {
        const n = new Set(prev);
        sendable.forEach((id) => n.delete(id));
        return n;
      });
    }
  }

  function toggleSelected(id: string, on: boolean) {
    setSelected((prev) => { const n = new Set(prev); if (on) n.add(id); else n.delete(id); return n; });
  }

  async function mark(ids: string[], paid: boolean) {
    if (!member || ids.length === 0) return;
    setBusy((prev) => new Set([...prev, ...ids]));
    // Optimistic update.
    setRows((prev) =>
      (prev ?? []).map((r) =>
        ids.includes(r.id)
          ? { ...r, paid_at: paid ? new Date().toISOString() : null, paid_by: paid ? member.id : null }
          : r,
      ),
    );
    try {
      await setSpiffsPaid(ids, paid, member.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Update failed");
      load(); // resync on error
    } finally {
      setBusy((prev) => {
        const n = new Set(prev);
        ids.forEach((id) => n.delete(id));
        return n;
      });
    }
  }

  const filtered = useMemo(() => {
    const all = rows ?? [];
    if (filter === "unpaid") return all.filter((r) => !r.paid_at);
    if (filter === "paid") return all.filter((r) => !!r.paid_at);
    return all;
  }, [rows, filter]);

  const weeks = useMemo<WeekGroup[]>(() => {
    const map = new Map<string, WeekGroup>();
    for (const r of filtered) {
      const monday = mondayOf(etDateOf(r.claimed_at));
      const g = map.get(monday) ?? { monday, rows: [], unpaidCents: 0, paidCents: 0, sentCents: 0, waitingCents: 0 };
      g.rows.push(r);
      if (r.paid_at) g.paidCents += r.amount_cents;
      else if (r.books_bill_id) g.sentCents += r.amount_cents;
      else if (!r.approved_at) g.waitingCents += r.amount_cents;
      else g.unpaidCents += r.amount_cents;
      map.set(monday, g);
    }
    return [...map.values()].sort((a, b) => (a.monday < b.monday ? 1 : -1));
  }, [filtered]);

  const totals = useMemo(() => {
    const all = rows ?? [];
    let unpaid = 0,
      paid = 0;
    for (const r of all) {
      if (r.paid_at) paid += r.amount_cents;
      else unpaid += r.amount_cents;
    }
    return { unpaid, paid };
  }, [rows]);

  return (
    <div>
      {/* Oct 19, 2026 — the SPIFF statement emails (one per person per pay day,
          sent by the database when a SPIFF is marked paid). Finance only. */}
      {statementsAllowed && (
        <div className="mb-4">
          <CommissionStatementsCard kind="spiff" />
        </div>
      )}

      {/* Summary + filter */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-2">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">Unpaid</div>
            <div className="text-base font-extrabold text-amber-800">{fmtUSD(totals.unpaid / 100)}</div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">Paid</div>
            <div className="text-base font-extrabold text-emerald-800">{fmtUSD(totals.paid / 100)}</div>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-400 mr-1">Window</span>
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWin(w.key)}
                className={
                  "px-2.5 py-1.5 rounded-full text-[12px] font-semibold border transition " +
                  (win === w.key
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
                }
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {(["unpaid", "paid", "all"] as PaidFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={
                  "px-3 py-1.5 rounded-full text-[12px] font-semibold border capitalize transition " +
                  (filter === f
                    ? "bg-bardin-orange text-white border-bardin-orange"
                    : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
                }
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 mb-3 -mt-1">
        Showing SPIFFs claimed in the selected window plus <span className="font-semibold text-amber-600">all unpaid</span> SPIFFs (no matter how old), so nothing owed is ever hidden.
        {" "}<span className="text-slate-500">Last week's SPIFFs come in Monday at noon and wait for <strong>Alex's approval</strong> (on the ASM board; Chad or Greg when he hasn't). Approving makes one bill per person in Books; Shannon pays it there on Tuesday and the rows read paid once Books reports back.</span>
      </p>

      {sendAllowed && selected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs">
          <span className="font-semibold text-sky-800">{selected.size} selected · {fmtUSD((rows ?? []).filter((r) => selected.has(r.id)).reduce((s, r) => s + r.amount_cents, 0) / 100)}</span>
          <button
            onClick={() => sendToBooks([...selected])}
            disabled={[...selected].some((id) => busy.has(id))}
            className="px-2.5 py-1 rounded bg-sky-600 text-white text-[11px] font-semibold hover:bg-sky-700 disabled:opacity-50"
          >
            Approve selected & send to Books
          </button>
          <button onClick={() => setSelected(new Set())} className="text-[11px] text-slate-500 hover:underline">Clear</button>
        </div>
      )}

      {err && <div className="bg-red-50 text-red-700 text-xs p-2 rounded mb-3">⚠ {err}</div>}

      {rows === null ? (
        <div className="text-sm text-slate-500">Loading SPIFFs…</div>
      ) : weeks.length === 0 ? (
        <div className="text-sm text-slate-500 bg-slate-50 rounded p-6 text-center">
          No {filter === "all" ? "" : filter} SPIFFs.
        </div>
      ) : (
        <div className="space-y-5">
          {weeks.map((w) => {
            // Oct 27, 2026 — waiting rows are approved (= sent to Books); an approved
            // row on no bill (legacy) keeps Shannon's Mark paid.
            const waitingIds = w.rows.filter((r) => isWaiting(r)).map((r) => r.id);
            const unpaidIds = w.rows.filter((r) => !r.paid_at && !r.books_bill_id && !!r.approved_at).map((r) => r.id);
            return (
              <div key={w.monday} className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between gap-3 bg-slate-50 px-4 py-2.5 border-b border-slate-100">
                  <div className="text-sm font-semibold text-bardin-ink">{fmtWeek(w.monday)}</div>
                  <div className="flex items-center gap-3 text-[12px]">
                    {w.waitingCents > 0 && (
                      <span className="text-orange-700 font-semibold">{fmtUSD(w.waitingCents / 100)} waiting for Alex</span>
                    )}
                    {w.unpaidCents > 0 && (
                      <span className="text-amber-700 font-semibold">{fmtUSD(w.unpaidCents / 100)} unpaid</span>
                    )}
                    {w.sentCents > 0 && (
                      <span className="text-sky-700">{fmtUSD(w.sentCents / 100)} sent to Books</span>
                    )}
                    {w.paidCents > 0 && (
                      <span className="text-emerald-700">{fmtUSD(w.paidCents / 100)} paid</span>
                    )}
                    {waitingIds.length > 0 && sendAllowed && (
                      <button
                        onClick={() => sendToBooks(waitingIds)}
                        disabled={waitingIds.some((id) => busy.has(id))}
                        title="Approve this week's waiting SPIFFs: one Books bill per person; Shannon pays them in Books"
                        className="px-2.5 py-1 rounded bg-sky-600 text-white text-[11px] font-semibold hover:bg-sky-700 disabled:opacity-50"
                      >
                        Approve week & send to Books ({waitingIds.length})
                      </button>
                    )}
                    {unpaidIds.length > 0 && (
                      <button
                        onClick={() => mark(unpaidIds, true)}
                        title="Legacy: mark the week paid here without a Books bill"
                        className="px-2.5 py-1 rounded bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700"
                      >
                        Mark week paid ({unpaidIds.length})
                      </button>
                    )}
                  </div>
                </div>
                <div className="divide-y divide-slate-100">
                  {w.rows.map((r) => {
                    const isPaid = !!r.paid_at;
                    const onBill = !isPaid && !!r.books_bill_id;
                    const waiting = isWaiting(r);
                    const bill = r.books_bill_id ? bills.bills.get(r.books_bill_id) ?? null : null;
                    const rowBusy = busy.has(r.id);
                    const isOverride = r.source === "asm_override";
                    return (
                      <div
                        key={r.id}
                        className={"flex items-center gap-3 px-4 py-2.5 " + (isPaid ? "bg-emerald-50/40" : onBill ? "bg-sky-50/40" : "")}
                      >
                        {onBill ? (
                          <span className="h-4 w-4 flex items-center justify-center text-sky-600 text-xs" title="Sent to Books">→</span>
                        ) : waiting && !sendAllowed ? (
                          <span className="h-4 w-4 flex items-center justify-center text-orange-500 text-xs" title="Waiting for Alex's approval">⏳</span>
                        ) : isPaid || !waiting ? (
                          <input
                            type="checkbox"
                            checked={isPaid}
                            disabled={rowBusy}
                            onChange={(e) => mark([r.id], e.target.checked)}
                            className="h-4 w-4 accent-emerald-600"
                            aria-label={isPaid ? "Mark unpaid" : "Mark paid"}
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={selected.has(r.id)}
                            disabled={rowBusy}
                            onChange={(e) => toggleSelected(r.id, e.target.checked)}
                            className="h-4 w-4 accent-sky-600"
                            aria-label="Select to approve and send to Books"
                          />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate text-bardin-ink">
                            {r.recipient_name}
                            <span className="ml-1.5 text-[10px] font-semibold uppercase text-slate-400">
                              {ROLE_SHORT[r.recipient_role] ?? r.recipient_role}
                            </span>
                            {isOverride && (
                              <span className="ml-1.5 align-middle inline-block rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">
                                ASM 50%
                              </span>
                            )}
                            {r.name === FIVE_STAR_REVIEW_SPIFF.name && (
                              <span className="ml-1.5 align-middle inline-block rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
                                ⭐ 5★ review
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 truncate">
                            {r.name}
                            {r.job_address ? ` · ${r.job_address}` : ""}
                            {` · ${fmtDay(r.claimed_at)}`}
                            {isPaid && r.paid_at ? ` · paid ${fmtDay(r.paid_at)}` : ""}
                          </div>
                        </div>
                        <div className="text-sm font-semibold whitespace-nowrap text-bardin-ink">
                          {fmtUSD(r.amount_cents / 100)}
                        </div>
                        {waiting ? (
                          <span
                            title="Alex approves it on the ASM board (Chad or Greg when he hasn't); approving sends it to Books"
                            className="px-2.5 py-1 rounded text-[11px] font-semibold whitespace-nowrap bg-orange-100 text-orange-700"
                          >
                            waiting for Alex
                          </span>
                        ) : onBill ? (
                          <span
                            title={bill ? billChipText(bill) : "Sent to Books — Shannon pays it there"}
                            className={"px-2.5 py-1 rounded text-[11px] font-semibold whitespace-nowrap max-w-[220px] truncate " + BILL_CHIP_CLASS[bill ? billChipTone(bill) : "sent"]}
                          >
                            {bill ? billChipText(bill) : "sent to Books"}
                          </span>
                        ) : (
                          <button
                            onClick={() => mark([r.id], !isPaid)}
                            disabled={rowBusy}
                            title={isPaid ? "Back to unpaid" : "Legacy: mark paid here without a Books bill"}
                            className={
                              "px-2.5 py-1 rounded text-[11px] font-semibold border whitespace-nowrap disabled:opacity-50 " +
                              (isPaid
                                ? "border-slate-200 text-slate-500 hover:bg-slate-50"
                                : "border-emerald-600 text-emerald-700 hover:bg-emerald-50")
                            }
                          >
                            {isPaid ? "Unpay" : "Mark paid"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
